import { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api-client';
import { renderEmailTemplate, type EmailTemplate, type EmailTemplateKind, type SupplierTemplate } from '../../lib/email-template';
import type { OurContact } from '../../lib/our-contact';
import type { TermsTemplate, TermsTemplateKind } from '../../lib/terms-template';
import { Field, Select } from '../ui/Input';

function useSettingsLists() {
  const [supplier, setSupplier] = useState<SupplierTemplate[]>([]);
  const [email, setEmail] = useState<EmailTemplate[]>([]);
  const [terms, setTerms] = useState<TermsTemplate[]>([]);
  useEffect(() => {
    void apiGet<{ settings?: { supplierTemplates?: SupplierTemplate[]; emailTemplates?: EmailTemplate[]; termsTemplates?: TermsTemplate[] } }>('/api/settings').then((data) => {
      setSupplier(data.settings?.supplierTemplates || []);
      setEmail(data.settings?.emailTemplates || []);
      setTerms(data.settings?.termsTemplates || []);
    }).catch(() => undefined);
  }, []);
  return { supplier, email, terms };
}

export function SupplierTemplateSelect({ onApply }: { onApply: (contact: OurContact) => void }) {
  const { supplier } = useSettingsLists();
  if (!supplier.length) return null;
  return (
    <Field label="Supplier details">
      <Select value="" onChange={(event) => {
        const row = supplier.find((item) => item.id === event.target.value);
        if (!row) return;
        onApply({ contactName: row.contactName, contactRole: row.contactRole, contactEmail: row.contactEmail, contactPhone: row.contactPhone });
      }}>
        <option value="">Select a template</option>
        {supplier.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </Select>
    </Field>
  );
}

export function TermsTemplateSelect({ kind, onApply }: {
  kind: TermsTemplateKind;
  onApply: (row: TermsTemplate) => void;
}) {
  const { terms } = useSettingsLists();
  const rows = terms.filter((row) => row.kind === kind);
  if (!rows.length) return null;
  return (
    <Field label="Terms template">
      <Select value="" onChange={(event) => {
        const row = rows.find((item) => item.id === event.target.value);
        if (row) onApply(row);
      }}>
        <option value="">Select a template</option>
        {rows.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </Select>
    </Field>
  );
}

export function EmailTemplateSelect({ kind, values, onApply }: {
  kind: EmailTemplateKind;
  values: { number?: string; customer?: string; title?: string; pos?: string[] };
  onApply: (subject: string, body: string) => void;
}) {
  const { email } = useSettingsLists();
  const rows = email.filter((row) => row.kind === kind);
  if (!rows.length) return null;
  return (
    <Field label="Email template">
      <Select value="" onChange={(event) => {
        const row = rows.find((item) => item.id === event.target.value);
        if (!row) return;
        onApply(renderEmailTemplate(row.subject, values), renderEmailTemplate(row.body, values));
      }}>
        <option value="">Select a template</option>
        {rows.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </Select>
    </Field>
  );
}
